// src/components/aiondemand/ActionPreview.jsx

import { useEffect, useState, useRef, useCallback } from 'react';
import {
  ArrowLeft, MapPin, Eye, Loader2, CheckCircle2,
  AlertCircle, Play, RefreshCw,
} from 'lucide-react';

import { getActionById } from '../../actions/registry.js';
import { fetchEntitledImagery } from '../../actions/wellheadDetection.js';
import { useAIOnDemandStore } from '../../stores/useAIOnDemandStore';
import { useProjectStore } from '../../stores/useProjectStore';
import { fetchWithAuth } from '../../api/fetchWithAuth';
import { formatAcquisitionDate } from '../../layers/imagery/formatImageryDate';

const COUNT_DEBOUNCE_MS = 300;
const LAUNCH_ACK_MS = 3000;

export default function ActionPreview({ actionId, mapRef, getScope, onBack, onSelectJob }) {
  const action = getActionById(actionId);

  // Sync run state
  const syncRun = useAIOnDemandStore((s) => s.syncRun);
  const startSyncRun = useAIOnDemandStore((s) => s.startSyncRun);
  const completeSyncRun = useAIOnDemandStore((s) => s.completeSyncRun);
  const failSyncRun = useAIOnDemandStore((s) => s.failSyncRun);
  const resetSyncRun = useAIOnDemandStore((s) => s.resetSyncRun);

  // Async launch state
  const recentLaunch = useAIOnDemandStore((s) => s.recentLaunch);
  const recordLaunch = useAIOnDemandStore((s) => s.recordLaunch);
  const clearLaunch = useAIOnDemandStore((s) => s.clearLaunch);
  const appendJob = useAIOnDemandStore((s) => s.appendJob);

  const projectId = useProjectStore((s) => s.activeProjectId);
  const projectName = useProjectStore((s) => s.activeProject?.name ?? null);

  // Explicit imagery selection (wellhead detection). Lives in the store so the
  // run payload and window.__stores.aiOnDemand see the same value.
  const selectedDatasetId = useAIOnDemandStore((s) => s.selectedDatasetId);
  const setSelectedDatasetId = useAIOnDemandStore((s) => s.setSelectedDatasetId);

  const needsImagery = Boolean(action?.needsImagerySelection);

  // On/off options the action declares (registry `options`). Keyed by option
  // key and read with the declared default, so an untouched switch sends its
  // default rather than nothing.
  const [optionValues, setOptionValues] = useState({});
  const resolvedOptions = Object.fromEntries(
    (action?.options ?? []).map((o) => [o.key, optionValues[o.key] ?? o.default]),
  );

  // Entitled imagery options for the current scope (null = not yet loaded).
  const [imageryOptions, setImageryOptions] = useState(null);
  const [imageryLoading, setImageryLoading] = useState(false);
  const [imageryError, setImageryError] = useState(null);
  const imageryReqIdRef = useRef(0);

  // Count state
  const [count, setCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [countError, setCountError] = useState(null);
  const countReqIdRef = useRef(0);
  const debounceTimerRef = useRef(null);

  const fetchCount = useCallback(async () => {
    if (!action?.preview?.getCount) {
      setCount(null);
      return;
    }
    const scope = getScope?.();
    if (!scope) {
      setCount(null);
      return;
    }

    const reqId = ++countReqIdRef.current;
    setCountLoading(true);
    setCountError(null);

    try {
      const n = await action.preview.getCount({ scope, fetchWithAuth });
      if (reqId !== countReqIdRef.current) return;
      setCount(n);
    } catch (err) {
      if (reqId !== countReqIdRef.current) return;
      console.error('[AIOnDemand] scope count failed', err);
      setCountError(String(err?.message ?? err));
      setCount(null);
    } finally {
      if (reqId === countReqIdRef.current) setCountLoading(false);
    }
  }, [action, getScope]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  // Load entitled imagery options for the scope (wellhead only). Applies the
  // default rule (D2): exactly one option → auto-select it; more than one →
  // leave unset (user must pick); zero → clear. A stale selection that's no
  // longer in the option set is cleared.
  const loadImagery = useCallback(async () => {
    if (!needsImagery) return;
    const scope = getScope?.();
    if (!scope) {
      setImageryOptions(null);
      return;
    }

    const reqId = ++imageryReqIdRef.current;
    setImageryLoading(true);
    setImageryError(null);

    try {
      const list = await fetchEntitledImagery({ scope, fetchWithAuth });
      if (reqId !== imageryReqIdRef.current) return;

      // Newest-first defensively (server already orders by acquisition_date desc).
      const sorted = [...list].sort((a, b) =>
        String(b.acquisition_date ?? '').localeCompare(String(a.acquisition_date ?? '')));
      setImageryOptions(sorted);

      const current = useAIOnDemandStore.getState().selectedDatasetId;
      const stillValid = current && sorted.some((d) => d.id === current);

      if (sorted.length === 1) {
        setSelectedDatasetId(sorted[0].id);           // exactly one → auto-select
      } else if (!stillValid) {
        setSelectedDatasetId(null);                   // >1 with no valid pick, or 0
      }
    } catch (err) {
      if (reqId !== imageryReqIdRef.current) return;
      console.error('[AIOnDemand] imagery lookup failed', err);
      setImageryError(String(err?.message ?? err));
      setImageryOptions([]);
      setSelectedDatasetId(null);
    } finally {
      if (reqId === imageryReqIdRef.current) setImageryLoading(false);
    }
  }, [needsImagery, getScope, setSelectedDatasetId]);

  useEffect(() => { loadImagery(); }, [loadImagery]);

  useEffect(() => {
    const map = mapRef?.current;
    if (!map) return;
    const scope = getScope?.();
    if (!scope || scope.kind !== 'viewport') return;

    const onMoveEnd = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        fetchCount();
        loadImagery();
      }, COUNT_DEBOUNCE_MS);
    };

    map.on('moveend', onMoveEnd);
    return () => {
      map.off('moveend', onMoveEnd);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [mapRef, getScope, fetchCount, loadImagery]);

  // Auto-dismiss launch ack
  useEffect(() => {
    if (!recentLaunch || recentLaunch.actionId !== actionId) return;
    const t = setTimeout(() => clearLaunch(), LAUNCH_ACK_MS);
    return () => clearTimeout(t);
  }, [recentLaunch, actionId, clearLaunch]);

  if (!action) {
    return <div className="p-3 text-xs text-red-400">Unknown action: {actionId}</div>;
  }

  const scope = getScope?.() ?? null;
  const hasCount = typeof count === 'number';
  const isAsync = action.kind === 'model_run';

  const isSyncRunForThis = syncRun?.actionId === actionId;
  const syncStatus = isSyncRunForThis ? syncRun.status : 'idle';

  const launchAckForThis = recentLaunch?.actionId === actionId ? recentLaunch : null;

  // Imagery gate (wellhead): a run needs an explicit, entitled selection.
  const imageryReady = !needsImagery || Boolean(selectedDatasetId);

  // Scope gate: an action may declare that it only runs on a saved AOI.
  // Declared in the registry rather than special-cased here, so the panel stays
  // generic and the next such action needs no change to this component.
  const aoiReady = !action.requiresAoi || scope?.kind === 'aoi';

  const canRun = Boolean(scope) && hasCount && count > 0 && imageryReady && aoiReady
    && (isAsync ? !launchAckForThis : syncStatus !== 'running');

  // Tooltip text when the run is blocked specifically by imagery selection.
  let runDisabledReason = '';
  if (!aoiReady) {
    runDisabledReason = action.requiresAoiReason
      ?? 'This action requires a saved AOI. Pick one under Scope above.';
  }
  if (needsImagery && !selectedDatasetId) {
    if (imageryOptions && imageryOptions.length === 0) {
      runDisabledReason = 'Your organization is not entitled to imagery intersecting this scope.';
    } else if (imageryOptions && imageryOptions.length > 1) {
      runDisabledReason = 'Select an imagery source';
    } else if (imageryLoading) {
      runDisabledReason = 'Loading imagery sources…';
    }
  }

  async function handleRun() {
    if (!canRun) return;

    if (isAsync) {
      try {
        const result = await action.run({
          scope,
          fetchWithAuth,
          projectId,
          aoiId: scope.aoi?.id ?? null,
          datasetId: selectedDatasetId ?? null,
        });
        recordLaunch(actionId, result.jobId);
        appendJob({
          id: result.jobId,
          status: result.status,
          task_key: action.id,
          model_key: action.name,
          external_job_id: result.externalJobId,
          tiles_completed: 0,
          processed_tile_count: 0,
          aoi_id: scope.aoi?.id ?? null,
          project_id: projectId,
          created_at: new Date().toISOString(),
          started_at: new Date().toISOString(),
        });
      } catch (err) {
        console.error(`[AIOnDemand] ${actionId} launch failed`, err);
        startSyncRun(actionId);
        failSyncRun(err);
      }
      return;
    }

    // Sync path
    startSyncRun(actionId);
    try {
      const result = await action.run({ scope, fetchWithAuth, projectName, options: resolvedOptions });
      completeSyncRun(result);
    } catch (err) {
      console.error(`[AIOnDemand] ${actionId} failed`, err);
      failSyncRun(err);
    }
  }

  const Icon = action.icon;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-3 pt-2 pb-1 shrink-0">
        <button onClick={onBack} className="flex items-center gap-1 text-[10px] text-fg-secondary hover:text-slate-300 transition">
          <ArrowLeft size={11} />
          Actions
        </button>
      </div>

      <div className="px-3 pb-2 shrink-0">
        <div className="flex items-center gap-1.5">
          <Icon size={13} className="text-accent-action" />
          <h3 className="text-xs font-semibold text-slate-100">{action.name}</h3>
        </div>
        <p className="mt-1 text-[10px] text-fg-secondary leading-relaxed">
          {action.description}
        </p>
      </div>

      <div className="px-3 pb-2 shrink-0">
        <div className="text-[9px] font-semibold tracking-[0.14em] font-mono uppercase text-fg-secondary mb-1">
          Will Run On
        </div>
        <ScopeSummary scope={scope} />
      </div>

      <div className="px-3 pb-3 shrink-0">
        <ScopeCount
          action={action}
          scope={scope}
          count={count}
          loading={countLoading}
          error={countError}
          onRefresh={fetchCount}
        />
      </div>

      {action.options?.length > 0 && (
        <div className="px-3 pb-3 shrink-0 flex flex-col gap-2">
          {action.options.map((o) => (
            <OptionSwitch
              key={o.key}
              option={o}
              checked={resolvedOptions[o.key]}
              disabled={syncStatus === 'running'}
              onChange={(v) => setOptionValues((prev) => ({ ...prev, [o.key]: v }))}
            />
          ))}
        </div>
      )}

      {needsImagery && (
        <div className="px-3 pb-3 shrink-0">
          <ImagerySelect
            options={imageryOptions}
            loading={imageryLoading}
            error={imageryError}
            selectedId={selectedDatasetId}
            onSelect={setSelectedDatasetId}
            onRetry={loadImagery}
          />
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {isAsync ? (
          <AsyncRunStatus
            launchAck={launchAckForThis}
            failedRun={isSyncRunForThis && syncStatus === 'failed' ? syncRun : null}
            onSelectJob={onSelectJob}
          />
        ) : (
          <RunStatus status={syncStatus} activeRun={isSyncRunForThis ? syncRun : null} />
        )}
      </div>

      {/* The AOI gate points at a control OUTSIDE this component — the
          panel-level Scope picker above — so it has to be visible rather than a
          tooltip on a disabled button, which is neither discoverable nor
          reliably hoverable. */}
      {!aoiReady && (
        <div className="px-3 pb-2 shrink-0">
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-2">
            <MapPin size={11} className="mt-0.5 shrink-0 text-amber-400" />
            <p className="text-[10px] leading-relaxed text-amber-200/90">{runDisabledReason}</p>
          </div>
        </div>
      )}

      <div className="px-3 py-2 border-t border-slate-800 shrink-0 flex items-center justify-between gap-2">
        <RunHint scope={scope} count={count} loading={countLoading} />

        {!isAsync && (syncStatus === 'complete' || syncStatus === 'failed') ? (
          <div className="flex items-center gap-2">
            <button onClick={resetSyncRun} className="text-[10px] text-fg-secondary hover:text-slate-300">
              Reset
            </button>
            <button onClick={handleRun} disabled={!canRun} title={!canRun && runDisabledReason ? runDisabledReason : undefined} className={runButtonClasses(canRun)}>
              <Play size={10} /> Run again
            </button>
          </div>
        ) : (
          <button onClick={handleRun} disabled={!canRun} title={!canRun && runDisabledReason ? runDisabledReason : undefined} className={runButtonClasses(canRun)}>
            {(!isAsync && syncStatus === 'running') ? (
              <><Loader2 size={10} className="animate-spin" /> Running…</>
            ) : isAsync && launchAckForThis ? (
              <><CheckCircle2 size={10} /> Started</>
            ) : (
              <><Play size={10} /> Run</>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function ScopeSummary({ scope }) {
  if (!scope) {
    return (
      <div className="px-2.5 py-2 rounded-md border border-slate-800 bg-bg text-[11px] text-fg-secondary italic">
        Resolving scope…
      </div>
    );
  }

  if (scope.kind === 'aoi') {
    return (
      <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-accent-info/40 bg-accent-info/10 text-accent-info text-xs">
        <MapPin size={11} className="shrink-0" />
        <span className="flex-1 truncate font-medium">{scope.aoi.name}</span>
        <span className="text-[10px] text-fg-secondary shrink-0">AOI</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-slate-700 bg-bg text-slate-300 text-xs">
      <Eye size={11} className="shrink-0" />
      <span className="flex-1 font-medium">Current viewport</span>
      <span className="text-[10px] text-fg-secondary shrink-0">Map bounds</span>
    </div>
  );
}

function ScopeCount({ action, scope, count, loading, error, onRefresh }) {
  const countLabel = action.preview?.countLabel ?? ((n) => `item${n === 1 ? '' : 's'}`);

  if (error) {
    return (
      <div className="text-[11px] text-red-400 flex items-center gap-1.5">
        <AlertCircle size={11} />
        <span className="flex-1">Couldn't count: {error}</span>
        <button onClick={onRefresh} className="text-fg-secondary hover:text-slate-300">
          <RefreshCw size={10} />
        </button>
      </div>
    );
  }

  if (loading && count === null) {
    return (
      <div className="text-[11px] text-fg-secondary flex items-center gap-1.5">
        <Loader2 size={11} className="animate-spin" />
        Counting…
      </div>
    );
  }

  if (count === null) return null;

  if (count === 0) {
    return (
      <div className="space-y-0.5">
        <div className="text-[11px] text-slate-400">
          No {countLabel(0)} in {scope?.kind === 'aoi' ? 'this AOI' : 'this viewport'}.
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 text-[11px] text-status-busy">
      {loading && <Loader2 size={10} className="animate-spin text-fg-secondary" />}
      <span>
        <span className="font-semibold">{count.toLocaleString()}</span>{' '}
        {countLabel(count)}{' '}
        <span className="text-fg-secondary">in {scope?.kind === 'aoi' ? 'AOI' : 'viewport'}</span>
      </span>
    </div>
  );
}

function RunHint({ scope, count, loading }) {
  if (loading) return <span className="text-[10px] text-fg-secondary">Checking scope…</span>;
  if (!scope) return <span className="text-[10px] text-fg-secondary">Map not ready.</span>;
  if (count === 0) return <span className="text-[10px] text-fg-secondary">Nothing to run on.</span>;
  return <span className="text-[10px] text-fg-secondary">&nbsp;</span>;
}

function RunStatus({ status, activeRun }) {
  if (status === 'idle') {
    return <p className="text-[10px] text-fg-secondary italic leading-relaxed">Runs synchronously. The report will download when complete.</p>;
  }
  if (status === 'running') {
    return (
      <div className="flex items-center gap-2 text-[11px] text-status-busy">
        <Loader2 size={12} className="animate-spin" />
        Generating report…
      </div>
    );
  }
  if (status === 'complete') {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-[11px] text-accent-info">
          <CheckCircle2 size={12} /> Report downloaded
        </div>
        {activeRun?.result?.filename && (
          <div className="text-[10px] text-fg-secondary pl-[18px] truncate">{activeRun.result.filename}</div>
        )}
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-[11px] text-red-400">
          <AlertCircle size={12} /> Run failed
        </div>
        {activeRun?.error && (
          <div className="text-[10px] text-red-400/80 pl-[18px] leading-relaxed whitespace-pre-line max-h-52 overflow-y-auto">{activeRun.error}</div>
        )}
      </div>
    );
  }
  return null;
}

function AsyncRunStatus({ launchAck, failedRun, onSelectJob }) {
  if (failedRun) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-[11px] text-red-400">
          <AlertCircle size={12} /> Launch failed
        </div>
        {failedRun.error && (
          <div className="text-[10px] text-red-400/80 pl-[18px] leading-relaxed whitespace-pre-line max-h-52 overflow-y-auto">{failedRun.error}</div>
        )}
      </div>
    );
  }

  if (launchAck) {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-[11px] text-accent-info">
          <CheckCircle2 size={12} /> Job started
        </div>
        <div className="text-[10px] text-fg-secondary pl-[18px] leading-relaxed">
          Tracking in Jobs. You'll be notified when it completes.
        </div>
        <button
          onClick={() => onSelectJob?.(launchAck.jobId)}
          className="ml-[18px] text-[10px] text-accent-info hover:underline"
        >
          View job →
        </button>
      </div>
    );
  }

  return (
    <p className="text-[10px] text-fg-secondary italic leading-relaxed">
      Runs in the background. You'll be notified when complete.
    </p>
  );
}

// acquisition_date is stored as TEXT ("2025_12"). Format for display; the raw
// value drives sort order upstream.
// Delegates to the canonical formatter. This was a hand-rolled copy of it,
// one of SEVEN, and the day-precision change had to be made in each — see
// layers/imagery/formatImageryDate.js. Only the empty-value contract stays
// local, because callers differ on what a missing date should read as.
function formatAcqDate(raw) {
  if (!raw) return 'Unknown date';
  return formatAcquisitionDate(raw);
}

function OptionSwitch({ option, checked, disabled, onChange }) {
  const id = `action-option-${option.key}`;
  return (
    <div className="flex items-start gap-2">
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={option.description ? `${id}-desc` : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`mt-0.5 relative inline-flex h-3.5 w-6 shrink-0 items-center rounded-full border transition
          disabled:opacity-40
          ${checked ? 'bg-accent-action/70 border-accent-action' : 'bg-slate-800 border-slate-600'}`}
      >
        <span
          className={`inline-block h-2.5 w-2.5 rounded-full bg-slate-100 transition-transform
            ${checked ? 'translate-x-[11px]' : 'translate-x-[1px]'}`}
        />
      </button>
      <div className="min-w-0">
        <label htmlFor={id} className="block text-[10px] font-medium text-slate-200 cursor-pointer">
          {option.label}
        </label>
        {option.description && (
          <p id={`${id}-desc`} className="text-[10px] text-fg-secondary leading-relaxed">
            {option.description}
          </p>
        )}
      </div>
    </div>
  );
}

function ImagerySelect({ options, loading, error, selectedId, onSelect, onRetry }) {
  const label = (
    <div className="text-[9px] font-semibold tracking-[0.14em] font-mono uppercase text-fg-secondary mb-1">
      Imagery Source
    </div>
  );

  if (error) {
    return (
      <div>
        {label}
        <div className="text-[11px] text-red-400 flex items-center gap-1.5">
          <AlertCircle size={11} />
          <span className="flex-1">Couldn't load imagery: {error}</span>
          <button onClick={onRetry} className="text-fg-secondary hover:text-slate-300">
            <RefreshCw size={10} />
          </button>
        </div>
      </div>
    );
  }

  if (loading && options === null) {
    return (
      <div>
        {label}
        <div className="text-[11px] text-fg-secondary flex items-center gap-1.5">
          <Loader2 size={11} className="animate-spin" />
          Loading imagery sources…
        </div>
      </div>
    );
  }

  if (options && options.length === 0) {
    return (
      <div>
        {label}
        <div className="px-2.5 py-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-[11px] text-amber-300 leading-relaxed">
          Your organization is not entitled to any imagery intersecting this scope.
        </div>
      </div>
    );
  }

  if (!options) return null;

  const singleAuto = options.length === 1;

  return (
    <div>
      {label}
      <select
        value={selectedId ?? ''}
        onChange={(e) => onSelect(e.target.value || null)}
        disabled={singleAuto}
        className="w-full px-2.5 py-1.5 rounded-md border border-slate-700 bg-bg text-slate-200 text-[11px] focus:outline-none focus:border-accent-info/60 disabled:opacity-70"
      >
        {!singleAuto && <option value="">Select an imagery source…</option>}
        {options.map((d) => (
          <option key={d.id} value={d.id}>
            {`${d.name} — ${formatAcqDate(d.acquisition_date)} · ${d.sku_code ?? '—'}`}
          </option>
        ))}
      </select>
      {singleAuto && (
        <div className="mt-1 text-[10px] text-fg-secondary">
          Only entitled source in scope — selected automatically.
        </div>
      )}
    </div>
  );
}

function runButtonClasses(enabled) {
  return [
    'px-3 py-1 rounded-md border text-[11px] font-medium',
    'flex items-center gap-1 transition',
    enabled
      ? 'border-accent-action/40 bg-accent-action/10 text-accent-action hover:bg-accent-action/20'
      : 'border-slate-800 text-slate-600 opacity-60 cursor-not-allowed',
  ].join(' ');
}